import { Suspense, use, useEffect, useMemo, useState, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar/Navbar";
import Footer from "@/components/Footer/Footer";
import NotFound from "@/pages/NotFound/NotFound";
import { getPost, formatPostDate, loadPostBody, relatedPosts } from "@/data/posts";
import { authorProfileUrl } from "@/data/authors";
import { useLocale } from "@/i18n/useLocale";
import { useGsapReveal } from "@/hooks/useGsapReveal";
import HireModalProvider from "@/components/HireModal/HireModalProvider";
import { useHireModal } from "@/components/HireModal/HireModalContext";
import { trackEvent } from "@/services/gtag";
import { NEWS_WIDGETS } from "@/components/NewsWidgets/registry";
import AudioWidget from "@/components/AudioWidget/AudioWidget";
import { scrollToSection } from "@/services/scrollToSection";
import "./News.css";

/**
 * Nota individual.
 *
 * El cuerpo se inyecta con dangerouslySetInnerHTML porque viene de convertir
 * Markdown a HTML. El origen es de confianza: son archivos del propio repo,
 * escritos por el equipo, no entrada de usuarios anonimos. Si en algun momento
 * el contenido pasa a venir de un admin abierto a terceros, este HTML tiene que
 * sanitizarse antes de renderizarse.
 */
/**
 * Id del primer widget interactivo de la nota (`<div class="post-widget"
 * id="...">`), para ofrecer el salto directo desde la barra de datos.
 */
function firstWidgetId(html: string): string | undefined {
  return /<div class="post-widget" id="([^"]+)"/.exec(html)?.[1];
}

/**
 * Monta un widget dentro de un marcador del cuerpo de la nota. Marca el host
 * como `is-live` cuando el widget ya esta en pantalla, para ocultar el texto
 * de respaldo (lo que ven los buscadores y quien navega sin JavaScript).
 */
function WidgetPortal({ host, children }: { host: HTMLElement; children: ReactNode }) {
  useEffect(() => {
    host.classList.add("is-live");
    return () => host.classList.remove("is-live");
  }, [host]);

  return createPortal(children, host);
}

function NewsPostContent() {
  const { t, path, locale } = useLocale();
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPost(slug) : undefined;
  const related = slug ? relatedPosts(slug, locale) : [];
  // El cuerpo baja aparte de los datos de la nota (ver loadPostBody). Ya viene
  // cargado en el primer render -preloadRoute lo espera antes de hidratar y de
  // prerenderizar-, y en una navegacion dentro del sitio suspende hasta que
  // llega.
  const html = post ? use(loadPostBody(post.slug)) : "";
  const factsRef = useGsapReveal<HTMLElement>({ from: "up", deps: [slug] });
  const navigate = useNavigate();
  const hire = useHireModal();

  // Los simuladores se montan sobre los marcadores `[data-widget]` que la nota
  // deja en su HTML. El body se remonta por slug (key) para que la referencia
  // cambie con cada nota y los marcadores se relean con el DOM ya actualizado.
  // En el prerender y en la primera pasada del cliente no hay hosts, asi que
  // la hidratacion no ve diferencias.
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);
  const widgetHosts = useMemo(
    () => (bodyEl ? Array.from(bodyEl.querySelectorAll<HTMLElement>("[data-widget]")) : []),
    [bodyEl],
  );

  // El objeto de dangerouslySetInnerHTML tiene que ser estable entre renders:
  // con uno nuevo en cada render React reescribe el innerHTML, los marcadores
  // se reemplazan por nodos nuevos y los widgets quedan montados en nodos que
  // ya no estan en el documento.
  const bodyHtml = useMemo(() => ({ __html: html }), [html]);

  // Los links del cuerpo vienen de Markdown: son <a> comunes, no <Link>, asi que
  // sin esto cada link interno -a otra nota, a un caso, a /#contacto- recargaba
  // toda la pagina en vez de navegar dentro de la SPA. Se deja pasar el click
  // normal del navegador cuando corresponde: con modificadores (abrir en otra
  // pestaña), links externos o con target propio.
  function handleBodyClick(e: MouseEvent<HTMLDivElement>) {
    if (e.defaultPrevented || e.button !== 0) return;

    const control = (e.target as HTMLElement).closest<HTMLElement>("a, button");
    if (!control) return;

    // Los llamados a la accion del cuerpo (data-cta="...") se miden antes de
    // cualquier otra cosa, incluso los que abren otra pestaña (WhatsApp) o se
    // hacen con Ctrl/Cmd: es el dato que dice que nota convierte y con que
    // boton. cta_id y post_slug alcanzan para armar el informe en GA4.
    if (control.dataset.cta) {
      trackEvent("cta_click", {
        cta_id: control.dataset.cta,
        post_slug: post?.slug ?? "",
        link_url: control.getAttribute("href") ?? (control.dataset.hire ? `hire:${control.dataset.hire}` : ""),
      });
    }

    // <button data-hire="administrativo">: abre el pedido rapido de empleado
    // virtual (nombre + celular) ahi mismo, sin sacar a la persona de la nota.
    const employeeId = control.dataset.hire;
    if (employeeId) {
      e.preventDefault();
      hire.open(employeeId, post?.title);
      return;
    }

    const anchor = control.closest("a");
    if (!anchor) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const href = anchor.getAttribute("href");

    // Ancla dentro de la misma nota (#simulador-caudal). Con el comportamiento
    // nativo el router ve el cambio de hash y App.tsx lleva la pagina al
    // inicio: se resuelve aca con el mismo scroll suave que usa el resto del
    // sitio.
    if (href && href.length > 1 && href.startsWith("#")) {
      e.preventDefault();
      scrollToSection(href.slice(1));
      return;
    }

    if (!href || !href.startsWith("/") || href.startsWith("//")) return;
    if (anchor.target && anchor.target !== "_self") return;

    e.preventDefault();
    navigate(href);
  }

  // Un slug inexistente devuelve el 404 real, con su status, en vez de una
  // pagina vacia que los buscadores indexarian como valida.
  if (!post) {
    return <NotFound />;
  }

  const simulatorId = firstWidgetId(html);
  const authorUrl = authorProfileUrl(post.author);

  return (
    <div className="news-post">
      <Navbar />

      <main id="main-content">
        {/* Mismo masthead negro que el listado: el logo del navbar es claro y
            necesita fondo oscuro detras mientras la pagina esta arriba de todo. */}
        <header className="news-post__masthead">
          <div className="news-post__masthead-inner">
            <nav aria-label={locale === "en" ? "Breadcrumb" : "Migas de pan"}>
              <Link to={path("news")} className="news-post__back">
                {t.news.back}
              </Link>
            </nav>

          <div className="news-post__meta">
            <span className="news-post__tag">{post.tag}</span>
            <time dateTime={post.date} className="news-post__date">
              {formatPostDate(post.date, locale)}
            </time>
          </div>

          <h1 className="news-post__title">{post.title}</h1>
          <p className="news-post__summary">{post.summary}</p>
        </div>
      </header>

      <div className="news-post__content">
        {/* Datos de la nota en una sola barra horizontal arriba, sobre el borde
            del masthead: categoria, fecha, autor y tiempo de lectura sin
            robarle una columna entera a la lectura. Si la nota trae un
            simulador, el salto directo vive aca mismo. */}
        <aside
          className="news-post__facts"
          ref={factsRef}
          aria-label={locale === "en" ? "Article details" : "Datos de la nota"}
        >
          <div className="news-post__fact-card">
            <div className="news-post__fact-item">
              <p className="news-post__fact-label">{t.news.factCategory}</p>
              <p className="news-post__fact-value">{post.tag}</p>
            </div>

            <div className="news-post__fact-item">
              <p className="news-post__fact-label">{t.news.factDate}</p>
              <p className="news-post__fact-value">{formatPostDate(post.date, locale)}</p>
            </div>

            <div className="news-post__fact-item">
              <p className="news-post__fact-label">{t.news.factAuthor}</p>
              <p className="news-post__fact-value">
                {authorUrl ? (
                  <a
                    className="news-post__author-link"
                    href={authorUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={
                      locale === "en"
                        ? `${post.author}, Instagram profile (opens in a new tab)`
                        : `${post.author}, perfil de Instagram (se abre en una pestaña nueva)`
                    }
                  >
                    {post.author}
                    <span aria-hidden="true"> ↗</span>
                  </a>
                ) : (
                  post.author
                )}
              </p>
            </div>

            <div className="news-post__fact-item">
              <p className="news-post__fact-label">{t.news.factReading}</p>
              <p className="news-post__fact-value">
                {post.readingMinutes} {t.news.readingTime}
              </p>
            </div>

            {simulatorId && (
              <a
                className="news-post__jump"
                href={`#${simulatorId}`}
                onClick={(e) => {
                  e.preventDefault();
                  trackEvent("cta_click", {
                    cta_id: "post-jump-simulator",
                    post_slug: post.slug,
                    link_url: `#${simulatorId}`,
                  });
                  scrollToSection(simulatorId);
                }}
              >
                {locale === "en" ? "Try the simulator" : "Probar el simulador"}
                <span aria-hidden="true"> ↓</span>
              </a>
            )}
          </div>
        </aside>

        <div className="news-post__layout">
          <article className="news-post__main">
            {/* Version en audio de la nota: se oculta sola si todavia no hay. */}
            <AudioWidget key={`audio-${post.slug}`} articleId={post.slug} />

            <div
              key={post.slug}
              ref={setBodyEl}
              className="news-post__body"
              onClick={handleBodyClick}
              dangerouslySetInnerHTML={bodyHtml}
            />

            {widgetHosts.map((host, i) => {
              const Widget = NEWS_WIDGETS[host.dataset.widget ?? ""];

              return Widget ? (
                <Suspense key={`${post.slug}-${i}`} fallback={null}>
                  <WidgetPortal host={host}>
                    <Widget />
                  </WidgetPortal>
                </Suspense>
              ) : null;
            })}
          </article>
        </div>
      </div>

      {/* Una nota que termina sin salida es un callejon: el lector se va. Esto
          va DESPUES del cierre de la nota -que ya tiene su llamado a contacto-
          para no competir con el: primero la conversion, despues seguir
          leyendo. */}
      {related.length > 0 && (
        <aside className="related" aria-labelledby="related-titulo">
          <div className="related__inner">
            <h2 className="related__title" id="related-titulo">
              {t.news.alsoRead}
            </h2>

            <ul className="related__list">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link to={`${path("news")}/${r.slug}`} className="related__item">
                    <div className="related__item-head">
                      <span className="related__tag">{r.tag}</span>
                      <time dateTime={r.date} className="related__date">
                        {formatPostDate(r.date, locale)}
                      </time>
                    </div>
                    <h3 className="related__item-title">{r.title}</h3>
                    {/* La bajada ya existia en los datos y nunca se mostraba
                        aca: la tarjeta quedaba en titulo + tiempo de lectura,
                        sin ningun gancho para decidir si vale la pena leerla. */}
                    <p className="related__item-summary">{r.summary}</p>
                    <span className="related__reading">
                      {r.readingMinutes} {t.news.readingTime}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      )}

      </main>
      <Footer />
    </div>
  );
}

/** El proveedor vive aca y no en App: solo las notas abren el modal de pedido. */
function NewsPost() {
  return (
    <HireModalProvider>
      <NewsPostContent />
    </HireModalProvider>
  );
}

export default NewsPost;
